import { useRef, useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, PenLine } from "lucide-react";

/**
 * Lightweight HTML5 canvas signature pad. Exposes `getDataUrl()` + `clear()`
 * via ref. Touch + mouse aware. Returns null if pad is empty.
 */
const SignaturePad = forwardRef(function SignaturePad({ height = 180, label = "Sign here" }, ref) {
    const canvasRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const [hasInk, setHasInk] = useState(false);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const ratio = window.devicePixelRatio || 1;
        c.width = c.offsetWidth * ratio;
        c.height = height * ratio;
        const ctx = c.getContext("2d");
        ctx.scale(ratio, ratio);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 2;
        ctx.strokeStyle = getComputedStyle(c).getPropertyValue("--foreground").trim() || "#111";
    }, [height]);

    const pos = (e) => {
        const c = canvasRef.current;
        const r = c.getBoundingClientRect();
        const t = e.touches?.[0];
        const x = (t ? t.clientX : e.clientX) - r.left;
        const y = (t ? t.clientY : e.clientY) - r.top;
        return { x, y };
    };

    const start = (e) => {
        e.preventDefault();
        const { x, y } = pos(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(x, y);
        setDrawing(true);
    };
    const move = (e) => {
        if (!drawing) return;
        e.preventDefault();
        const { x, y } = pos(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasInk(true);
    };
    const end = () => setDrawing(false);

    const clear = () => {
        const c = canvasRef.current;
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, c.width, c.height);
        setHasInk(false);
    };

    useImperativeHandle(ref, () => ({
        clear,
        isEmpty: () => !hasInk,
        getDataUrl: () => (hasInk ? canvasRef.current.toDataURL("image/png") : null),
    }), [hasInk]);

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <PenLine className="w-3 h-3" />{label}
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={clear}
                        data-testid="signature-clear">
                    <Eraser className="w-3.5 h-3.5 mr-1" />Clear
                </Button>
            </div>
            <canvas
                ref={canvasRef}
                data-testid="signature-canvas"
                className="w-full bg-secondary/40 rounded-md border border-border touch-none cursor-crosshair"
                style={{ height: `${height}px` }}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            />
        </div>
    );
});

export default SignaturePad;
